import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuthHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem('token')}` });

const CertificatePreviewPage = () => {
  const navigate = useNavigate();
  const certificateRef = useRef(null);
  const [certificateData, setCertificateData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCert = async () => {
      try {
        const userRes = await axios.get(`${API}/auth/me`, { headers: getAuthHeaders() });
        const user = userRes.data;
        
        // Get progress which includes certificate info
        const progressRes = await axios.get(`${API}/progress/dashboard`, { headers: getAuthHeaders() });
        const progress = progressRes.data;
        
        setCertificateData({
          studentName: `${user.first_name} ${user.last_name}`,
          completionDate: progress.certificate_issued_at 
            ? new Date(progress.certificate_issued_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
            : new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
          certificateNumber: progress.certificate_number || 'Pending',
          creditHours: "24",
          finalExamScore: progress.final_exam_score ? `${progress.final_exam_score}%` : 'N/A',
          instructorName: "Gilbert Tafoya",
          instructorLicense: "403558",
          organizationName: "Colorado Bail Enforcement Agency LLC"
        });
      } catch {
        setCertificateData({
          studentName: "Loading...",
          completionDate: "",
          certificateNumber: "",
          creditHours: "24",
          finalExamScore: "",
          instructorName: "Gilbert Tafoya",
          instructorLicense: "403558",
          organizationName: "Colorado Bail Enforcement Agency LLC"
        });
      } finally {
        setLoading(false);
      }
    };
    fetchCert();
  }, []);

  const handlePrint = () => {
    window.print();
  };

  if (loading || !certificateData) {
    return <div className="min-h-screen flex items-center justify-center"><p className="animate-pulse">Loading certificate...</p></div>;
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8 print:bg-white print:py-0">
      {/* Controls - Hidden when printing */}
      <div className="max-w-4xl mx-auto px-4 mb-6 print:hidden">
        <div className="flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={handlePrint}
              className="flex items-center gap-2"
            >
              <Printer className="w-4 h-4" />
              Print Certificate
            </Button>
            <Button
              onClick={async () => {
                try {
                  const res = await axios.get(`${API}/progress/my-certificate/download`, {
                    headers: getAuthHeaders(),
                    responseType: 'blob'
                  });
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `certificate_${certificateData?.certificateNumber || 'download'}.pdf`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                } catch { alert('Certificate PDF not available yet. It must be approved by an administrator first.'); }
              }}
              className="flex items-center gap-2"
              data-testid="download-cert-pdf-btn"
            >
              <Download className="w-4 h-4" />
              Download PDF
            </Button>
          </div>
        </div>
        <p className="text-center text-sm text-gray-500 mt-4">
          This is a preview of the certificate design. Actual certificates will be generated upon course completion and admin approval.
        </p>
      </div>

      {/* Certificate */}
      <div 
        ref={certificateRef}
        className="max-w-4xl mx-auto bg-white shadow-2xl print:shadow-none"
        style={{ aspectRatio: '11/8.5' }}
      >
        <div className="relative w-full h-full p-8 print:p-12">
          {/* Decorative Border */}
          <div className="absolute inset-4 border-4 border-[#1e3a5f] pointer-events-none" />
          <div className="absolute inset-6 border-2 border-[#c9a227] pointer-events-none" />
          
          {/* Corner Decorations */}
          <div className="absolute top-8 left-8 w-16 h-16 border-l-4 border-t-4 border-[#c9a227]" />
          <div className="absolute top-8 right-8 w-16 h-16 border-r-4 border-t-4 border-[#c9a227]" />
          <div className="absolute bottom-8 left-8 w-16 h-16 border-l-4 border-b-4 border-[#c9a227]" />
          <div className="absolute bottom-8 right-8 w-16 h-16 border-r-4 border-b-4 border-[#c9a227]" />

          {/* Content */}
          <div className="relative z-10 h-full flex flex-col items-center justify-between py-8 px-12">
            {/* Header */}
            <div className="text-center">
              {/* Badge Image */}
              <div className="flex justify-center mb-2">
                <img 
                  src="/images/badge-transparent.png" 
                  alt="Bail Enforcement Agent Badge" 
                  className="w-20 h-20 object-contain"
                />
              </div>
              <h3 className="text-sm tracking-[0.3em] text-[#1e3a5f] uppercase mb-1">
                {certificateData.organizationName}
              </h3>
              <h1 className="text-4xl font-black text-[#1e3a5f] tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>
                CERTIFICATE OF COMPLETION
              </h1>
            </div>

            {/* Body */}
            <div className="text-center flex-1 flex flex-col justify-center py-6">
              <p className="text-gray-600 text-lg mb-4">This is to certify that</p>
              
              <h2 className="text-4xl font-bold text-[#1e3a5f] mb-4 border-b-2 border-[#c9a227] pb-2 px-8 inline-block" style={{ fontFamily: 'Georgia, serif' }}>
                {certificateData.studentName}
              </h2>
              
              <p className="text-gray-600 text-lg mb-6 max-w-xl mx-auto">
                has successfully completed all requirements of the
              </p>
              
              <h3 className="text-2xl font-bold text-[#1e3a5f] mb-2 uppercase tracking-wide">
                Colorado Bail Enforcement
              </h3>
              <h3 className="text-2xl font-bold text-[#1e3a5f] mb-6 uppercase tracking-wide">
                Certification Course
              </h3>

              {/* Course Details */}
              <div className="flex justify-center gap-12 text-sm mb-4">
                <div className="text-center">
                  <p className="text-gray-500 uppercase tracking-wide text-xs">Credit Hours</p>
                  <p className="text-[#1e3a5f] font-bold text-xl">{certificateData.creditHours}</p>
                </div>
                <div className="text-center">
                  <p className="text-gray-500 uppercase tracking-wide text-xs">Final Exam Score</p>
                  <p className="text-[#1e3a5f] font-bold text-xl">{certificateData.finalExamScore}</p>
                </div>
                <div className="text-center">
                  <p className="text-gray-500 uppercase tracking-wide text-xs">Completion Date</p>
                  <p className="text-[#1e3a5f] font-bold text-xl">{certificateData.completionDate}</p>
                </div>
              </div>

              {/* POST Compliance Badge */}
              <div className="inline-block bg-[#1e3a5f]/5 border border-[#1e3a5f]/20 rounded px-4 py-2 mt-2">
                <p className="text-xs text-[#1e3a5f] font-medium">
                  Exceeds Colorado POST E-212 Training Requirements (16 hours minimum)
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="w-full">
              {/* Signatures */}
              <div className="flex justify-between items-end mb-6 px-8">
                <div className="text-center">
                  <div className="w-48 border-b-2 border-gray-400 mb-2">
                    <p className="text-xl italic text-[#1e3a5f] pb-1" style={{ fontFamily: 'Brush Script MT, cursive' }}>
                      Gilbert Tafoya
                    </p>
                  </div>
                  <p className="text-sm font-semibold text-[#1e3a5f]">{certificateData.instructorName}</p>
                  <p className="text-xs text-gray-500">License #{certificateData.instructorLicense}</p>
                  <p className="text-xs text-gray-500">Course Instructor</p>
                </div>

                {/* Seal - Badge Image */}
                <div className="flex flex-col items-center">
                  <div className="w-24 h-24 flex items-center justify-center">
                    <img 
                      src="/images/badge.png" 
                      alt="Official Seal" 
                      className="w-20 h-20 object-contain"
                    />
                  </div>
                </div>

                <div className="text-center">
                  <div className="w-48 border-b-2 border-gray-400 mb-2">
                    <p className="text-xl italic text-[#1e3a5f] pb-1" style={{ fontFamily: 'Brush Script MT, cursive' }}>
                      {certificateData.completionDate}
                    </p>
                  </div>
                  <p className="text-sm font-semibold text-[#1e3a5f]">Date of Issue</p>
                  <p className="text-xs text-gray-500">&nbsp;</p>
                  <p className="text-xs text-gray-500">&nbsp;</p>
                </div>
              </div>

              {/* Certificate Number & Verification */}
              <div className="flex justify-between items-center border-t border-gray-200 pt-4 px-4">
                <div>
                  <p className="text-xs text-gray-500">Certificate Number</p>
                  <p className="text-sm font-mono font-bold text-[#1e3a5f]">{certificateData.certificateNumber}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">Verify at</p>
                  <p className="text-sm text-[#1e3a5f] font-medium">coloradobailenforcement.com/verify</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">Retain for 5 years</p>
                  <p className="text-xs text-gray-400">per Colorado DOI requirements</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          @page {
            size: landscape;
            margin: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default CertificatePreviewPage;
